import { css } from '@emotion/core'
import { SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { breakpoints, emptySpaceWidthBreakPoint } from '../constants/breakPoints'
import { coinImages, stripImages } from '../constants/stripImages'
import Image from './Image'
import { Link } from './Link'

interface ImageStripProps {
  displayedImages?: stripImages
  imageWidth?: string
  imageWidthSmallMedium?: string
  cycleDuration?: number
  withoutFade?: boolean
  heading?: string
  subtitle?: string
  linkText?: string
  linkUrl?: string
  cssStyles?: SerializedStyles
  children?: React.ReactNode
  withHeading2Style?: boolean
  withSubtitleStyle?: boolean
}

const ImageStrip: React.FunctionComponent<ImageStripProps> = ({
  displayedImages = coinImages,
  imageWidth = '10rem',
  imageWidthSmallMedium = '8.3rem',
  cycleDuration = 40,
  withoutFade = false,
  heading,
  subtitle,
  linkText,
  linkUrl,
  cssStyles = css``,
  children,
  withHeading2Style = false,
  withSubtitleStyle = false,
}) => {
  const { prePath, imgSources } = displayedImages
  return (
    <div css={[containerStyle(subtitle), cssStyles]}>
      {heading &&
        (withHeading2Style ? (
          <h2 className="heading2 center no-margin">{parse(heading)}</h2>
        ) : (
          <h3 className="heading3 center">{parse(heading)}</h3>
        ))}
      {subtitle && <div className={`${withSubtitleStyle ? 'subtitle' : 'body-1'} center`}>{parse(subtitle)}</div>}
      {linkText && linkUrl && (
        <Link className={`${withSubtitleStyle ? 'subtitle' : 'body-1'} blue-text`} href={linkUrl} withArrow>
          {linkText}
        </Link>
      )}
      {children}
      <div className="full-screen-relative" css={trackContainerStyle(imageWidth, imageWidthSmallMedium, withoutFade)}>
        <div css={trackStyle(imgSources.length, imageWidth, imageWidthSmallMedium, cycleDuration)}>
          {imgSources.map((imgSource) => (
            <Image
              src={`${prePath}${imgSource}.webp`}
              alt={imgSource.replace(/-/g, ' ')}
              key={imgSource}
              css={imageStyle(imageWidth, imageWidthSmallMedium)}
            />
          ))}
          {imgSources.map((imgSource) => (
            <Image
              src={`${prePath}${imgSource}.webp`}
              alt={imgSource.replace(/-/g, ' ')}
              key={imgSource}
              css={imageStyle(imageWidth, imageWidthSmallMedium)}
            />
          ))}
          {imgSources.map((imgSource) => (
            <Image
              src={`${prePath}${imgSource}.webp`}
              alt={imgSource.replace(/-/g, ' ')}
              key={imgSource}
              css={imageStyle(imageWidth, imageWidthSmallMedium)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ImageStrip

const containerStyle = (subtitle?: string) => css`
  margin: 8rem auto;
  text-align: center;

  .subtitle {
    margin: 1.5rem 0;
  }

  .body-1 {
    margin: 1rem 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 4rem auto;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 3rem auto;

    .heading2,
    .heading3,
    .subtitle,
    .body-1 {
      text-align: ${subtitle ? 'left' : 'center'} !important;
    }

    a {
      display: block;
    }

    ${!subtitle && '.heading2 { margin-bottom: 2rem !important }'};
  }
`

const trackContainerStyle = (imageWidth: string, imageWidthSmallMedium: string, withoutFade: boolean) => css`
  position: relative;
  height: ${imageWidth};
  margin: 4rem 0;
  overflow: hidden;

  ${!withoutFade &&
  `
    &::before,
    &::after {
      position: absolute;
      content: '';
      background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
      height: 100%;
      width: 20%;
      z-index: 1;
      top: 0;
    }

    &::before {
      left: -1px;
    }

    &::after {
      right: -1px;
      transform: rotateZ(180deg);
    }
  `}

  @media screen and (min-width: ${emptySpaceWidthBreakPoint}px) {
    margin: 3rem 0;
    padding: 0 2rem !important;
    left: 0 !important;
  }

  @media screen and (max-width: ${breakpoints.large}px) {
    margin: 3rem 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 2rem 0;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    height: ${imageWidthSmallMedium};
    margin: 1rem 0 5rem;
  }
`

const trackStyle = (
  quantityOfImages: number,
  imageWidth: string,
  imageWidthSmallMedium: string,
  cycleDuration: number
) => css`
  display: flex;
  width: calc(${imageWidth} * ${quantityOfImages} * 3);
  animation: scroll ${cycleDuration}s linear infinite;

  @keyframes scroll {
    0% {
      transform: translateX(calc(-${imageWidth} * ${quantityOfImages}));
    }
    100% {
      transform: translateX(calc((-2 * ${imageWidth}) * ${quantityOfImages}));
    }
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    width: calc(${imageWidthSmallMedium} * ${quantityOfImages} * 3);
    animation: scroll ${cycleDuration}s linear infinite;

    @keyframes scroll {
      0% {
        transform: translateX(calc(-${imageWidthSmallMedium} * ${quantityOfImages}));
      }
      100% {
        transform: translateX(calc(-2 * ${imageWidthSmallMedium} * ${quantityOfImages}));
      }
    }
  }
`

const imageStyle = (imageWidth: string, imageWidthSmallMedium: string) => css`
  height: ${imageWidth};
  width: ${imageWidth};
  margin-right: 1rem;
  border-radius: 0.5rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    height: ${imageWidthSmallMedium};
    width: ${imageWidthSmallMedium};
    margin-right: 0.5rem;
  }
`
